import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { TableCell, TableRow } from "@/components/ui/table";
import type { ItemDeTerreno } from "@/lib/terreno";
import { cn } from "@/lib/utils";

import { conteoDeRegistrosDelItem, etiquetaDelCruceDeAgua } from "./aguasTerreno";
import type { ModoDelTerreno } from "./cuadroDeConstrucciones";
import { AvisoDeAusenciaEnTerreno } from "./AvisoDeAusenciaEnTerreno";
import { NotaDeCreadaEnTerreno } from "./NotaDeCreadaEnTerreno";
import {
  ausenteEnTerreno,
  debeAvisarLaAusencia,
  type OpcionParaVincular,
} from "./maquinariasTerreno";
import { BloqueDeRegistros, ContadorDeRegistros } from "./RegistrosDeTerrenoDeLaFila";

/**
 * Lo que la línea necesita del terreno. Es la parte común de lo que tienen las
 * listas de máquinas y de instalaciones (`TerrenoDeLaLista` de cada una).
 */
export interface TerrenoDeLaFila {
  informeId: number;
  modo: ModoDelTerreno;
  encargado: string | null;
  onAbrirFoto: (uuid: string) => void;
  onVincular: (uuid: string, filaId: number) => void;
  /** «Quitar de la lista» del aviso de ausencia: borra la fila guardada. */
  onQuitar?: (filaId: number) => void;
  /** «Mantener» del aviso de ausencia: el servidor recuerda cuándo se decidió. */
  onMantener?: (filaId: number) => void;
}

interface FilaDeTerrenoDeLaFilaProps {
  terreno: TerrenoDeLaFila;
  /** Cómo se llama la fila en el encabezado del registro y en los nombres accesibles. */
  titulo: string;
  item: ItemDeTerreno | null;
  /** La fila tiene (o va a tener) un registro de terreno enlazado: lleva la insignia. */
  deTerreno: boolean;
  /** Solo en las filas TERRENO sin guardar: el uuid de lo que creó la App. */
  uuidPorEnlazar?: string;
  /** Con qué filas guardadas puede decir "es la misma que". */
  opciones: OpcionParaVincular[];
  /** Lo que dice el selector de "Es la misma que" sin elegir: "Elegir máquina…". */
  placeholderDelVinculo: string;
  abierto: boolean;
  ocupada: boolean;
  onAlternar: () => void;
  /**
   * La fila guardada que esta línea acompaña (no las filas TERRENO sin guardar):
   * lo que el aviso de ausencia necesita para quitarla o mantenerla.
   */
  /** Nació en la App y todavía no tiene datos de valorización: lleva la nota azul. */
  creadaSinValorizar?: boolean;
  guardada?: { id: number; fotos: number; ausenciaMantenidaEn: string | null };
  /** "máquina", "instalación": cómo se nombra en el diálogo de quitar. */
  queEs?: string;
  /** El demostrativo que concuerda con `queEs`. */
  esta?: "esta" | "este";
}

/**
 * La línea de debajo de cada fila de las tablas de máquinas e instalaciones:
 * de dónde viene (insignia "Terreno"), si el encargado dijo que no está en el
 * predio, cuánto trajo la visita (contador que abre el registro) y, si la fila
 * nació en la App y todavía no se guardó, qué falta y con qué fila ya cargada
 * se puede confundir.
 *
 * Es de LECTURA: el registro de terreno propone y nunca copia nada a la fila.
 * "No está en el predio" también — informa, no marca ni borra la fila.
 */
export function FilaDeTerrenoDeLaFila({
  terreno,
  titulo,
  item,
  deTerreno,
  uuidPorEnlazar,
  opciones,
  placeholderDelVinculo,
  abierto,
  ocupada,
  onAlternar,
  creadaSinValorizar = false,
  guardada,
  queEs = "fila",
  esta = "esta",
}: FilaDeTerrenoDeLaFilaProps) {
  const conteo = conteoDeRegistrosDelItem(item);
  const etiqueta = etiquetaDelCruceDeAgua(terreno.modo, conteo);
  const ausente = ausenteEnTerreno(item);
  const pideDatos = uuidPorEnlazar !== undefined;

  // EL AVISO VA CON LA FILA DESPLEGADA, o sola cuando no hay nada que
  // desplegar: una ausencia observada sin registros no tendría otra forma de
  // llegar a quien debe decidir.
  const avisarAusencia =
    guardada !== undefined &&
    terreno.onQuitar !== undefined &&
    terreno.onMantener !== undefined &&
    debeAvisarLaAusencia(item, guardada.ausenciaMantenidaEn) &&
    (abierto || conteo.total === 0);

  // Una fila hecha a mano, sin terreno que cruzar (módulo apagado) y sin nada
  // que decir, no dibuja una línea vacía.
  if (!deTerreno && !ausente && terreno.modo === "oculto") return null;

  return (
    <TableRow className="hover:bg-transparent">
      <TableCell colSpan={10} className="p-0">
        <div className="flex flex-wrap items-center gap-2.5 px-5 pb-2">
          {deTerreno ? (
            <span
              title="Nació en la App de terreno"
              className="bg-info-soft text-info-soft-foreground border-info-dot/30 inline-flex items-center gap-1.5 rounded-sm border px-2 py-0.5 text-[0.625rem] font-extrabold tracking-[0.05em] uppercase"
            >
              <span aria-hidden="true" className="bg-info-dot size-1.5 rounded-full" />
              Terreno
            </span>
          ) : null}

          {ausente ? (
            <span
              title="Lo indicó el encargado en terreno; no cambia los datos de la fila"
              className="bg-danger-soft text-danger-soft-foreground rounded-sm px-2 py-0.5 text-[0.6875rem] font-bold"
            >
              No está en el predio
            </span>
          ) : null}

          {conteo.total > 0 ? (
            <ContadorDeRegistros
              conteo={conteo}
              abierto={abierto}
              etiqueta={`Registro de terreno de ${titulo}`}
              onAlternar={onAlternar}
            />
          ) : etiqueta ? (
            <span
              className={cn(
                "rounded-sm px-[0.4375rem] py-0.5 text-[0.6875rem] font-bold",
                etiqueta === "Visita pendiente"
                  ? "bg-warning-soft text-warning-soft-foreground"
                  : "bg-muted text-ink-muted",
              )}
            >
              {etiqueta}
            </span>
          ) : null}

          {pideDatos && opciones.length > 0 ? (
            <label className="text-ink-body ml-auto inline-flex items-center gap-2 text-[0.71875rem] font-bold">
              Es la misma que
              <Select
                value=""
                disabled={ocupada}
                onValueChange={(clave) => terreno.onVincular(uuidPorEnlazar, Number(clave))}
              >
                <SelectTrigger
                  size="sm"
                  className="max-w-60 rounded-[0.5625rem] px-2 text-xs font-semibold"
                  aria-label={`Es la misma que, ${titulo}`}
                >
                  <SelectValue placeholder={placeholderDelVinculo} />
                </SelectTrigger>
                <SelectContent>
                  {opciones.map((opcion) => (
                    <SelectItem key={opcion.clave} value={opcion.clave}>
                      {opcion.etiqueta}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </label>
          ) : null}
        </div>

        {creadaSinValorizar && (abierto || conteo.total === 0 || pideDatos) ? (
          <NotaDeCreadaEnTerreno />
        ) : null}

        {avisarAusencia && guardada ? (
          <AvisoDeAusenciaEnTerreno
            queEs={queEs}
            esta={esta}
            nombre={titulo}
            fotos={guardada.fotos}
            ocupada={ocupada}
            onQuitar={() => terreno.onQuitar?.(guardada.id)}
            onMantener={() => terreno.onMantener?.(guardada.id)}
          />
        ) : null}

        {abierto && item ? (
          <BloqueDeRegistros
            informeId={terreno.informeId}
            item={item}
            titulo={titulo}
            encargado={terreno.encargado}
            onAbrirFoto={terreno.onAbrirFoto}
          />
        ) : null}
      </TableCell>
    </TableRow>
  );
}
